import React, { useState, useEffect, useCallback, memo } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import {
  ThumbUp,
  ThumbDown,
  AccessTime,
  Comment as CommentIcon,
  ExpandMore,
  ExpandLess,
  Close
} from '@mui/icons-material';
import useApi from 'hooks/useApi';

interface RatingDialogProps {
  open: boolean;
  transactionId: string | null;
  rating: 'good' | 'bad' | 'none' | null;
  comment: string;
  submittingRating: string | null;
  onClose: () => void;
  onCommentChange: (comment: string) => void;
  onSubmit: () => void;
}

const RatingDialog = memo<RatingDialogProps>(({ 
  open, 
  transactionId, 
  rating, 
  comment, 
  submittingRating,
  onClose, 
  onCommentChange, 
  onSubmit 
}) => {
  const handleCommentChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    onCommentChange(event.target.value);
  }, [onCommentChange]);

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      disableEnforceFocus
      disableAutoFocus
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">
          Rate Resource - {rating === 'good' ? 'Good' : rating === 'bad' ? 'Bad' : 'None'}
        </Typography>
        <IconButton 
          onClick={onClose}
          size="small"
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {rating === 'none' 
              ? "You're marking this resource as not rated for this transaction."
              : `You're rating this resource as ${rating} for this transaction.`
            }
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Comment (optional)"
            placeholder={
              rating === 'good' 
                ? "What was good about this resource?"
                : rating === 'bad'
                ? "What could be improved about this resource?"
                : "Any additional notes about this resource?"
            }
            value={comment}
            onChange={handleCommentChange}
            variant="outlined"
            sx={{ mb: 2 }}
            InputProps={{
              sx: {
                '& textarea': {
                  resize: 'vertical'
                }
              }
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={onClose}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button 
          onClick={onSubmit}
          variant="contained"
          disabled={submittingRating === transactionId}
          startIcon={submittingRating === transactionId ? <CircularProgress size={16} /> : undefined}
        >
          Submit Rating
        </Button>
      </DialogActions>
    </Dialog>
  );
});

RatingDialog.displayName = 'RatingDialog';

interface Transaction {
  id: string;
  key: string;
  displayName?: string;
  title?: string;
  createdAt: string;
  status?: string;
  rating?: {
    rating: 'good' | 'bad' | 'none';
    comment?: string; // Add comment field
    ratedBy: {
      id: string;
      uid: string;
      name: string;
      role: {
        id: string;
        uid: string;
        name: string;
      };
    } | string; // Support both new object format and old string format
    ratedAt: string;
  };
}

interface TransactionRatingProps {
  resourceId: string;
  userId: string;
  currentTransactionId?: string; // ID of the current transaction being viewed
  allowRating?: boolean; // Whether to allow rating (true only on details page)
  compact?: boolean; // Whether to use compact layout for dialogs
}

const TransactionRating: React.FC<TransactionRatingProps> = ({ 
  resourceId, 
  userId, 
  currentTransactionId,
  allowRating = false,
  compact = false 
}) => {
  const { getResourceTransactionsWithRatings, rateResource, showToast } = useApi();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [submittingRating, setSubmittingRating] = useState<string | null>(null);
  const [ratingDialog, setRatingDialog] = useState<{
    open: boolean;
    transactionId: string | null;
    rating: 'good' | 'bad' | 'none' | null;
    comment: string;
  }>({
    open: false,
    transactionId: null,
    rating: null,
    comment: ''
  });
  const [expandedComments, setExpandedComments] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadData();
  }, [resourceId]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getResourceTransactionsWithRatings(resourceId);

      if (response?.success) {
        setTransactions(response.data || []);
      }
    } catch (error) {
      console.error('Error loading transaction data:', error);
      showToast('Failed to load transaction data', 'error');
    } finally {
      setLoading(false);
    }
  }, [resourceId, getResourceTransactionsWithRatings, showToast]);

  const handleRating = useCallback(async (transactionId: string, rating: 'good' | 'bad' | 'none', comment?: string) => {
    try {
      setSubmittingRating(transactionId);
      
      // Now that API supports comments
      const response = await rateResource({
        resourceId,
        transactionId,
        rating,
        comment: comment || '',
        userId
      });

      if (response?.success) {
        const ratingMessage = rating === 'none' ? 'marked as not rated' : `rated as ${rating}`;
        showToast(`Resource ${ratingMessage} for current transaction`, 'success');
        // Reload data to get updated ratings
        await loadData();
        // Close dialog if open
        setRatingDialog({ open: false, transactionId: null, rating: null, comment: '' });
      } else {
        showToast('Failed to submit rating', 'error');
      }
    } catch (error) {
      console.error('Error submitting rating:', error);
      showToast('Failed to submit rating', 'error');
    } finally {
      setSubmittingRating(null);
    }
  }, [resourceId, userId, rateResource, showToast, loadData]);

  const openRatingDialog = useCallback((transactionId: string, rating: 'good' | 'bad' | 'none', existingComment?: string) => {
    setRatingDialog({
      open: true,
      transactionId,
      rating,
      comment: existingComment || ''
    });
  }, []);

  const handleDialogSubmit = useCallback(() => {
    if (ratingDialog.transactionId && ratingDialog.rating) {
      handleRating(ratingDialog.transactionId, ratingDialog.rating, ratingDialog.comment);
    }
  }, [ratingDialog, handleRating]);

  const handleDialogClose = useCallback(() => {
    setRatingDialog({ open: false, transactionId: null, rating: null, comment: '' });
  }, []);

  const handleCommentChange = useCallback((comment: string) => {
    setRatingDialog(prev => ({ ...prev, comment }));
  }, []);

  const toggleCommentExpansion = useCallback((transactionId: string) => {
    const newExpanded = new Set(expandedComments);
    if (newExpanded.has(transactionId)) {
      newExpanded.delete(transactionId);
    } else {
      newExpanded.add(transactionId);
    }
    setExpandedComments(newExpanded);
  }, [expandedComments]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Component to display comments
  const CommentDisplay: React.FC<{ comment?: string; transactionId: string; compact?: boolean; showAlways?: boolean }> = ({ 
    comment, 
    transactionId, 
    compact = false,
    showAlways = false 
  }) => {
    const hasComment = comment && comment.trim() !== '';
    
    // If showAlways is false and there's no comment, don't show anything
    if (!showAlways && !hasComment) return null;

    const isExpanded = expandedComments.has(transactionId);
    const shouldTruncate = hasComment && comment.length > 100;
    const displayComment = shouldTruncate && !isExpanded ? comment.substring(0, 100) + '...' : comment;

    return (
      <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
          <CommentIcon sx={{ fontSize: compact ? 14 : 16, color: 'text.secondary', mt: 0.2 }} />
          <Box sx={{ flex: 1 }}>
            {hasComment ? (
              <>
                <Typography 
                  variant={compact ? "caption" : "body2"} 
                  color="text.secondary"
                  sx={{ 
                    fontSize: compact ? '0.7rem' : '0.875rem',
                    lineHeight: 1.4,
                    wordBreak: 'break-word'
                  }}
                >
                  {displayComment}
                </Typography>
                {shouldTruncate && (
                  <Button
                    size="small"
                    onClick={() => toggleCommentExpansion(transactionId)}
                    sx={{ 
                      p: 0, 
                      minWidth: 'auto', 
                      fontSize: compact ? '0.65rem' : '0.75rem',
                      textTransform: 'none',
                      mt: 0.5
                    }}
                  >
                    {isExpanded ? (
                      <>
                        <ExpandLess sx={{ fontSize: 16, mr: 0.5 }} />
                        Show less
                      </>
                    ) : (
                      <>
                        <ExpandMore sx={{ fontSize: 16, mr: 0.5 }} />
                        Show more
                      </>
                    )}
                  </Button>
                )}
              </>
            ) : (
              <Typography 
                variant={compact ? "caption" : "body2"} 
                color="text.disabled"
                sx={{ 
                  fontSize: compact ? '0.7rem' : '0.875rem',
                  fontStyle: 'italic'
                }}
              >
                No comment
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  // Helper function to get rater information
  const getRaterInfo = (ratedBy: any) => {
    if (typeof ratedBy === 'string') {
      return { name: 'Unknown User', role: 'Unknown Role', isCurrentUser: ratedBy === userId };
    }
    
    if (ratedBy && typeof ratedBy === 'object') {
      return {
        name: ratedBy.name || 'Unknown User',
        role: ratedBy.role?.name || 'Unknown Role',
        isCurrentUser: ratedBy.id === userId || ratedBy.uid === userId
      };
    }
    
    return { name: 'Unknown User', role: 'Unknown Role', isCurrentUser: false };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <CircularProgress size={32} />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Loading transactions...
        </Typography>
      </Box>
    );
  }

  if (transactions.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        This resource has not been used in any transactions yet.
      </Alert>
    );
  }

  // Calculate rating totals
  const ratingCounts = transactions.reduce(
    (acc, transaction) => {
      if (transaction.rating && transaction.rating.rating !== 'none') {
        if (transaction.rating.rating === 'good') {
          acc.good += 1;
        } else if (transaction.rating.rating === 'bad') {
          acc.bad += 1;
        }
      }
      return acc;
    },
    { good: 0, bad: 0 }
  );

  const totalRated = ratingCounts.good + ratingCounts.bad;

  // Compact layout for dialogs
  if (compact) {
    return (
      <Box>
        {/* Compact Rating Summary */}
        <Box sx={{ 
          p: 1.5, 
          border: '1px solid', 
          borderColor: 'divider', 
          borderRadius: 1, 
          bgcolor: 'grey.50',
          mb: 2 
        }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Rating Summary
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ThumbUp sx={{ color: 'success.main', fontSize: 16 }} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {ratingCounts.good}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ThumbDown sx={{ color: 'error.main', fontSize: 16 }} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {ratingCounts.bad}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              ({totalRated}/{transactions.length} rated)
            </Typography>
            {totalRated > 0 && (
              <Typography variant="caption" color="success.main" sx={{ fontWeight: 500 }}>
                {((ratingCounts.good / totalRated) * 100).toFixed(0)}% positive
              </Typography>
            )}
          </Box>
        </Box>

        {/* Current Transaction Rating */}
        {currentTransactionId && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Current Transaction
            </Typography>
            {(() => {
              const currentTransaction = transactions.find(t => {
                const tAny = t as any;
                return String(currentTransactionId) === String(tAny.uid) ||
                       String(currentTransactionId) === String(tAny.transactionUid) ||
                       String(currentTransactionId) === String(tAny.transUid) ||
                       String(currentTransactionId) === String(t.id) ||
                       String(currentTransactionId) === String(t.key) ||
                       String(currentTransactionId) === String(tAny._id);
              });

              if (currentTransaction) {
                const existingRating = currentTransaction.rating;
                const raterInfo = existingRating ? getRaterInfo(existingRating.ratedBy) : null;
                
                // Case logic
                const isRatedByCurrentUser = existingRating && existingRating.rating !== 'none' && raterInfo?.isCurrentUser;
                const isRatedByOtherUser = existingRating && existingRating.rating !== 'none' && !raterInfo?.isCurrentUser;
                const isNotRated = !existingRating || existingRating.rating === 'none';

                return (
                  <Box sx={{ 
                    p: 1.5, 
                    border: '2px solid', 
                    borderColor: 'primary.main', 
                    borderRadius: 1, 
                    bgcolor: 'primary.50',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                        Transaction - {currentTransaction.displayName || currentTransaction.title || `Transaction ${currentTransaction.key}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Rated on - {formatDate(currentTransaction.createdAt)}
                      </Typography>
                    </Box>

                    {/* Loading Indicator */}
                    {submittingRating === currentTransaction.id && (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 0 }}>
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                      </Box>
                    )}

                    {/* Case 1: Rated by current user */}
                    {isRatedByCurrentUser && (
                      <Box>
                        {allowRating && (
                          <Box>
                            <Typography variant="caption" color="primary.main" sx={{ display: 'block', mb: 1 }}>
                              Change your rating:
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Button
                                size="small"
                                variant={existingRating.rating === 'good' ? 'contained' : 'outlined'}
                                color="success"
                                startIcon={<ThumbUp />}
                                onClick={() => openRatingDialog(currentTransaction.id, 'good', existingRating?.comment)}
                                disabled={submittingRating === currentTransaction.id}
                                sx={{ minWidth: 60, fontSize: '0.75rem' }}
                              >
                                Good
                              </Button>
                              <Button
                                size="small"
                                variant={existingRating.rating === 'bad' ? 'contained' : 'outlined'}
                                color="error"
                                startIcon={<ThumbDown />}
                                onClick={() => openRatingDialog(currentTransaction.id, 'bad', existingRating?.comment)}
                                disabled={submittingRating === currentTransaction.id}
                                sx={{ minWidth: 60, fontSize: '0.75rem' }}
                              >
                                Bad
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                startIcon={<AccessTime />}
                                onClick={() => openRatingDialog(currentTransaction.id, 'none', existingRating?.comment)}
                                disabled={submittingRating === currentTransaction.id}
                                sx={{ minWidth: 60, fontSize: '0.75rem' }}
                              >
                                None
                              </Button>
                            </Box>
                            {/* Show comment below rating buttons */}
                            <CommentDisplay 
                              comment={existingRating?.comment} 
                              transactionId={currentTransaction.id}
                              compact={true}
                              showAlways={true}
                            />
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* Case 2: Not rated */}
                    {isNotRated && (
                      <Box>
                        {allowRating && userId ? (
                          <Box>
                            <Typography variant="caption" color="primary.main" sx={{ display: 'block', mb: 1 }}>
                              Rate the resource for this transaction:
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                color="success"
                                startIcon={<ThumbUp />}
                                onClick={() => openRatingDialog(currentTransaction.id, 'good')}
                                disabled={submittingRating === currentTransaction.id}
                                sx={{ minWidth: 60, fontSize: '0.75rem' }}
                              >
                                Good
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={<ThumbDown />}
                                onClick={() => openRatingDialog(currentTransaction.id, 'bad')}
                                disabled={submittingRating === currentTransaction.id}
                                sx={{ minWidth: 60, fontSize: '0.75rem' }}
                              >
                                Bad
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                color="secondary"
                                startIcon={<AccessTime />}
                                onClick={() => openRatingDialog(currentTransaction.id, 'none')}
                                disabled={submittingRating === currentTransaction.id}
                                sx={{ 
                                  minWidth: 60, 
                                  fontSize: '0.75rem',
                                  bgcolor: 'grey.400',
                                  '&:hover': { bgcolor: 'grey.500' }
                                }}
                              >
                                None
                              </Button>
                            </Box>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              startIcon={<ThumbUp />}
                              disabled
                              sx={{ minWidth: 60, fontSize: '0.75rem' }}
                            >
                              Good
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<ThumbDown />}
                              disabled
                              sx={{ minWidth: 60, fontSize: '0.75rem' }}
                            >
                              Bad
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="secondary"
                              startIcon={<AccessTime />}
                              disabled
                              sx={{ 
                                minWidth: 60, 
                                fontSize: '0.75rem',
                                bgcolor: 'grey.400'
                              }}
                            >
                              None
                            </Button>
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* Case 3: Rated by another user */}
                    {isRatedByOtherUser && (
                      <Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Button
                            size="small"
                            variant={existingRating.rating === 'good' ? 'contained' : 'outlined'}
                            color="success"
                            startIcon={<ThumbUp />}
                            onClick={() => {
                              showToast(`The resource for this transaction is rated by ${raterInfo?.name || 'someone'} (${raterInfo?.role || 'Unknown Role'}).`, 'warning');
                            }}
                            sx={{ minWidth: 60, fontSize: '0.75rem' }}
                          >
                            Good
                          </Button>
                          <Button
                            size="small"
                            variant={existingRating.rating === 'bad' ? 'contained' : 'outlined'}
                            color="error"
                            startIcon={<ThumbDown />}
                            onClick={() => {
                              showToast(`The resource for this transaction is rated by ${raterInfo?.name || 'someone'} (${raterInfo?.role || 'Unknown Role'}).`, 'warning');
                            }}
                            sx={{ minWidth: 60, fontSize: '0.75rem' }}
                          >
                            Bad
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            startIcon={<AccessTime />}
                            onClick={() => {
                              showToast(`The resource for this transaction is rated by ${raterInfo?.name || 'someone'} (${raterInfo?.role || 'Unknown Role'}).`, 'warning');
                            }}
                            sx={{ minWidth: 60, fontSize: '0.75rem' }}
                          >
                            None
                          </Button>
                        </Box>
                        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                          Rated by {raterInfo?.name || 'someone'} ({raterInfo?.role || 'Unknown Role'}).
                        </Typography>
                      </Box>
                    )}

                    {/* {submittingRating === currentTransaction.id && (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                      </Box>
                    )} */}
                  </Box>
                );
              }
              return (
                <Typography variant="caption" color="text.secondary">
                  Current transaction not found in history
                </Typography>
              );
            })()}
          </Box>
        )}

        {/* Compact Transaction List */}
        {(() => {
          const otherTransactions = transactions.filter(transaction => {
            // Exclude current transaction from the list
            const transactionAny = transaction as any;
            return !(currentTransactionId && (
              String(currentTransactionId) === String(transactionAny.uid) ||
              String(currentTransactionId) === String(transactionAny.transactionUid) ||
              String(currentTransactionId) === String(transactionAny.transUid) ||
              String(currentTransactionId) === String(transaction.id) ||
              String(currentTransactionId) === String(transaction.key) ||
              String(currentTransactionId) === String(transactionAny._id)
            ));
          });

          return (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Other Transactions ({otherTransactions.length})
              </Typography>
              {otherTransactions.length > 0 ? (
                <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                  {otherTransactions
                    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                    .map((transaction) => {
                      const existingRating = transaction.rating;

                      return (
                        <Box 
                          key={transaction.id}
                          sx={{ 
                            p: 1, 
                            border: '1px solid', 
                            borderColor: 'divider',
                            borderRadius: 1, 
                            mb: 0.5,
                            bgcolor: 'background.paper'
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 400 }}>
                                Transaction - {transaction.displayName || transaction.title || `Transaction ${transaction.key}`}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Rated on - {formatDate(transaction.createdAt)}
                              </Typography>
                            </Box>
                            
                            <Box>
                              {existingRating && existingRating.rating !== 'none' ? (
                                <Box sx={{ textAlign: 'right' }}>
                                  <Chip
                                    icon={existingRating.rating === 'good' ? <ThumbUp /> : <ThumbDown />}
                                    label={existingRating.rating === 'good' ? 'Good' : 'Bad'}
                                    color={existingRating.rating === 'good' ? 'success' : 'error'}
                                    variant="outlined"
                                    size="small"
                                    sx={{ fontSize: '0.7rem', height: 20 }}
                                  />
                                  {(() => {
                                    const raterInfo = getRaterInfo(existingRating.ratedBy);
                                    return (
                                      <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem' }}>
                                          {raterInfo.isCurrentUser ? 'Your rating' : `By ${raterInfo.name}`}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.6rem' }}>
                                          ({raterInfo.role})
                                        </Typography>
                                      </Box>
                                    );
                                  })()}
                                </Box>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  Not rated
                                </Typography>
                              )}
                            </Box>
                          </Box>
                          {/* Show comment below the rating */}
                          {existingRating && existingRating.rating !== 'none' && (
                            <CommentDisplay 
                              comment={existingRating.comment} 
                              transactionId={transaction.id}
                              compact={true}
                              showAlways={true}
                            />
                          )}
                        </Box>
                      );
                    })}
                </Box>
              ) : (
                <Box sx={{ 
                  p: 2, 
                  textAlign: 'center', 
                  border: '1px dashed', 
                  borderColor: 'divider', 
                  borderRadius: 1,
                  bgcolor: 'grey.50'
                }}>
                  <Typography variant="caption" color="text.secondary">
                    No other transactions found for this resource
                  </Typography>
                </Box>
              )}
            </>
          );
        })()}

        {/* Rating Dialog */}
        <RatingDialog
          open={ratingDialog.open}
          transactionId={ratingDialog.transactionId}
          rating={ratingDialog.rating}
          comment={ratingDialog.comment}
          submittingRating={submittingRating}
          onClose={handleDialogClose}
          onCommentChange={handleCommentChange}
          onSubmit={handleDialogSubmit}
        />
      </Box>
    );
  }

  // Regular layout for full pages
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
        Transaction History & Ratings
      </Typography>
      
      {/* Rating Summary */}
      <Card variant="outlined" sx={{ mb: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
            Rating Summary
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ThumbUp sx={{ color: 'success.main', fontSize: 20 }} />
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Good: {ratingCounts.good}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ThumbDown sx={{ color: 'error.main', fontSize: 20 }} />
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Bad: {ratingCounts.bad}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Total Rated: {totalRated} of {transactions.length}
              </Typography>
            </Box>
          </Box>
          {totalRated > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {((ratingCounts.good / totalRated) * 100).toFixed(0)}% positive rating
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {/* Sort transactions to put current transaction first */}
        {transactions
          .sort((a, b) => {
            const aTransactionAny = a as any;
            const bTransactionAny = b as any;
            
            const aIsCurrentTransaction = currentTransactionId && (
              String(currentTransactionId) === String(aTransactionAny.uid) ||
              String(currentTransactionId) === String(aTransactionAny.transactionUid) ||
              String(currentTransactionId) === String(aTransactionAny.transUid) ||
              String(currentTransactionId) === String(a.id) ||
              String(currentTransactionId) === String(a.key) ||
              String(currentTransactionId) === String(aTransactionAny._id)
            );
            
            const bIsCurrentTransaction = currentTransactionId && (
              String(currentTransactionId) === String(bTransactionAny.uid) ||
              String(currentTransactionId) === String(bTransactionAny.transactionUid) ||
              String(currentTransactionId) === String(bTransactionAny.transUid) ||
              String(currentTransactionId) === String(b.id) ||
              String(currentTransactionId) === String(b.key) ||
              String(currentTransactionId) === String(bTransactionAny._id)
            );
            
            // Current transaction comes first
            if (aIsCurrentTransaction && !bIsCurrentTransaction) return -1;
            if (!aIsCurrentTransaction && bIsCurrentTransaction) return 1;
            
            // For non-current transactions, sort by date (newest first)
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          })
          .map((transaction) => {
          const existingRating = transaction.rating;
          const raterInfo = existingRating ? getRaterInfo(existingRating.ratedBy) : null;
          
          // Try multiple ways to match the current transaction
          const transactionAny = transaction as any;
          const isCurrentTransaction = currentTransactionId && (
            // Most likely matches
            String(currentTransactionId) === String(transactionAny.uid) ||
            String(currentTransactionId) === String(transactionAny.transactionUid) ||
            String(currentTransactionId) === String(transactionAny.transUid) ||
            // Fallback matches
            String(currentTransactionId) === String(transaction.id) ||
            String(currentTransactionId) === String(transaction.key) ||
            String(currentTransactionId) === String(transactionAny._id)
          );
          
          const canRate = (!existingRating || existingRating.rating === 'none') && userId && allowRating && isCurrentTransaction;
          
          // Case logic for current transaction
          const isRatedByCurrentUser = existingRating && existingRating.rating !== 'none' && raterInfo?.isCurrentUser;
          const isRatedByOtherUser = existingRating && existingRating.rating !== 'none' && !raterInfo?.isCurrentUser;
          const isNotRated = !existingRating || existingRating.rating === 'none';

          // Debug logging
          console.log('TransactionRating Debug:', {
            transaction,
            transactionId: transaction.id,
            transactionUid: transactionAny.uid,
            transactionKey: transaction.key,
            currentTransactionId,
            isCurrentTransaction,
            allowRating,
            canRate
          });

          return (
            <Card 
              key={transaction.id} 
              variant="outlined" 
              sx={{ 
                borderRadius: 2,
                // Highlight current transaction
                ...(isCurrentTransaction && {
                  borderColor: 'primary.main',
                  borderWidth: 2,
                  backgroundColor: 'primary.50',
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.15)'
                })
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {transaction.displayName || transaction.title || `Transaction ${transaction.key}`}
                      </Typography>
                      <Chip
                        label={isCurrentTransaction ? "Current Transaction" : "Other Transaction"}
                        color={isCurrentTransaction ? "primary" : "default"}
                        size="small"
                        variant={isCurrentTransaction ? "filled" : "outlined"}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <AccessTime sx={{ fontSize: 14 }} />
                      {formatDate(transaction.createdAt)}
                    </Typography>
                    
                    {transaction.status && (
                      <Chip 
                        label={transaction.status} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                        sx={{ mt: 1 }}
                      />
                    )}
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* Case 1: Rated by current user (full page) */}
                    {isCurrentTransaction && isRatedByCurrentUser ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                        <Chip
                          icon={existingRating.rating === 'good' ? <ThumbUp /> : <ThumbDown />}
                          label={existingRating.rating === 'good' ? 'Good' : 'Bad'}
                          color={existingRating.rating === 'good' ? 'success' : 'error'}
                          variant="filled"
                          size="small"
                        />
                        {raterInfo && (
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="caption" color="primary.main" sx={{ display: 'block', fontWeight: 500 }}>
                              Your rating
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                              {raterInfo.role}
                            </Typography>
                            {existingRating.ratedAt && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block' }}>
                                {formatDate(existingRating.ratedAt)}
                              </Typography>
                            )}
                          </Box>
                        )}
                        {allowRating && (
                          <Box sx={{ mt: 1, textAlign: 'right' }}>
                            <Typography variant="caption" color="primary.main" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
                              Change rating:
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                              <Button
                                size="small"
                                variant={existingRating.rating === 'good' ? 'contained' : 'outlined'}
                                color="success"
                                startIcon={<ThumbUp />}
                                onClick={() => openRatingDialog(transaction.id, 'good', existingRating?.comment)}
                                disabled={submittingRating === transaction.id}
                                sx={{ minWidth: 70 }}
                              >
                                Good
                              </Button>
                              <Button
                                size="small"
                                variant={existingRating.rating === 'bad' ? 'contained' : 'outlined'}
                                color="error"
                                startIcon={<ThumbDown />}
                                onClick={() => openRatingDialog(transaction.id, 'bad', existingRating?.comment)}
                                disabled={submittingRating === transaction.id}
                                sx={{ minWidth: 70 }}
                              >
                                Bad
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                startIcon={<AccessTime />}
                                onClick={() => openRatingDialog(transaction.id, 'none', existingRating?.comment)}
                                disabled={submittingRating === transaction.id}
                                sx={{ minWidth: 70 }}
                              >
                                None
                              </Button>
                            </Box>
                            {/* Show comment below rating buttons */}
                            <CommentDisplay 
                              comment={existingRating?.comment} 
                              transactionId={transaction.id}
                              compact={false}
                              showAlways={true}
                            />
                          </Box>
                        )}
                      </Box>
                    ) : 
                    
                    /* Case 2: Not rated (full page) */
                    isCurrentTransaction && isNotRated ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                        <Chip
                          icon={<AccessTime />}
                          label="None"
                          color="secondary"
                          variant="filled"
                          size="small"
                          sx={{ 
                            bgcolor: 'grey.400',
                            color: 'white',
                            '& .MuiChip-icon': { color: 'white' }
                          }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'right' }}>
                          Not rated
                        </Typography>
                        {allowRating && userId ? (
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="caption" color="primary.main" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
                              Rate this transaction:
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                              <Button
                                size="small"
                                variant="outlined"
                                color="success"
                                startIcon={<ThumbUp />}
                                onClick={() => openRatingDialog(transaction.id, 'good')}
                                disabled={submittingRating === transaction.id}
                                sx={{ minWidth: 70 }}
                              >
                                Good
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={<ThumbDown />}
                                onClick={() => openRatingDialog(transaction.id, 'bad')}
                                disabled={submittingRating === transaction.id}
                                sx={{ minWidth: 70 }}
                              >
                                Bad
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                color="secondary"
                                startIcon={<AccessTime />}
                                onClick={() => openRatingDialog(transaction.id, 'none')}
                                disabled={submittingRating === transaction.id}
                                sx={{ 
                                  minWidth: 70,
                                  bgcolor: 'grey.400',
                                  '&:hover': { bgcolor: 'grey.500' }
                                }}
                              >
                                None
                              </Button>
                            </Box>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              startIcon={<ThumbUp />}
                              disabled
                              sx={{ minWidth: 70 }}
                            >
                              Good
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<ThumbDown />}
                              disabled
                              sx={{ minWidth: 70 }}
                            >
                              Bad
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="secondary"
                              startIcon={<AccessTime />}
                              disabled
                              sx={{ 
                                minWidth: 70,
                                bgcolor: 'grey.400'
                              }}
                            >
                              None
                            </Button>
                          </Box>
                        )}
                      </Box>
                    ) : 
                    
                    /* Case 3: Rated by another user (full page) */
                    isCurrentTransaction && isRatedByOtherUser ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                        <Chip
                          icon={existingRating.rating === 'good' ? <ThumbUp /> : <ThumbDown />}
                          label={existingRating.rating === 'good' ? 'Good' : 'Bad'}
                          color={existingRating.rating === 'good' ? 'success' : 'error'}
                          variant="filled"
                          size="small"
                        />
                        {raterInfo && (
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="caption" color="primary.main" sx={{ display: 'block', fontWeight: 500 }}>
                              By {raterInfo.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                              {raterInfo.role}
                            </Typography>
                            {existingRating.ratedAt && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block' }}>
                                {formatDate(existingRating.ratedAt)}
                              </Typography>
                            )}
                          </Box>
                        )}
                        <Box sx={{ mt: 1, textAlign: 'right' }}>
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                            <Button
                              size="small"
                              variant={existingRating.rating === 'good' ? 'contained' : 'outlined'}
                              color="success"
                              startIcon={<ThumbUp />}
                              onClick={() => {
                                showToast(`The resource has been rated for this transaction by ${raterInfo?.name || 'someone'} (${raterInfo?.role || 'Unknown Role'}).`, 'warning');
                              }}
                              sx={{ minWidth: 70 }}
                            >
                              Good
                            </Button>
                            <Button
                              size="small"
                              variant={existingRating.rating === 'bad' ? 'contained' : 'outlined'}
                              color="error"
                              startIcon={<ThumbDown />}
                              onClick={() => {
                                showToast(`The resource has been rated for this transaction by ${raterInfo?.name || 'someone'} (${raterInfo?.role || 'Unknown Role'}).`, 'warning');
                              }}
                              sx={{ minWidth: 70 }}
                            >
                              Bad
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="secondary"
                              startIcon={<AccessTime />}
                              onClick={() => {
                                showToast(`Rated by ${raterInfo?.name || 'someone'} (${raterInfo?.role || 'Unknown Role'}).`, 'warning');
                              }}
                              sx={{ minWidth: 70 }}
                            >
                              None
                            </Button>
                          </Box>
                          <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                            Rated by {raterInfo?.name || 'someone'} ({raterInfo?.role || 'Unknown Role'})
                          </Typography>
                        </Box>
                      </Box>
                    ) : 
                    
                    /* Non-current transaction with existing rating */
                    existingRating && existingRating.rating !== 'none' ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                        <Chip
                          icon={existingRating.rating === 'good' ? <ThumbUp /> : <ThumbDown />}
                          label={existingRating.rating === 'good' ? 'Good' : 'Bad'}
                          color={existingRating.rating === 'good' ? 'success' : 'error'}
                          variant="filled"
                          size="small"
                        />
                        {raterInfo && (
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="caption" color="primary.main" sx={{ display: 'block', fontWeight: 500 }}>
                              {raterInfo.isCurrentUser ? 'Your rating' : `By ${raterInfo.name}`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                              {raterInfo.role}
                            </Typography>
                            {existingRating.ratedAt && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block' }}>
                                {formatDate(existingRating.ratedAt)}
                              </Typography>
                            )}
                          </Box>
                        )}
                        {/* Show comment below rating for non-current transactions */}
                        <CommentDisplay 
                          comment={existingRating?.comment} 
                          transactionId={transaction.id}
                          compact={false}
                          showAlways={true}
                        />
                      </Box>
                    ) : canRate ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {/* Different messaging for current vs non-current transactions */}
                        {isCurrentTransaction ? (
                          <Typography variant="caption" color="primary.main" sx={{ textAlign: 'center', mb: 1, fontWeight: 600 }}>
                            {existingRating && existingRating.rating === 'none' 
                              ? "Previously marked as 'Not Rated' - Rate this transaction:"
                              : "Rate this transaction:"
                            }
                          </Typography>
                        ) : (
                          existingRating && existingRating.rating === 'none' && (
                            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', mb: 1 }}>
                              Previously marked as "Not Rated" - Rate it now:
                            </Typography>
                          )
                        )}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            startIcon={<ThumbUp />}
                            onClick={() => handleRating(transaction.id, 'good')}
                            disabled={submittingRating === transaction.id}
                            sx={{ minWidth: 80 }}
                          >
                            Good
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<ThumbDown />}
                            onClick={() => handleRating(transaction.id, 'bad')}
                            disabled={submittingRating === transaction.id}
                            sx={{ minWidth: 80 }}
                          >
                            Bad
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            startIcon={<AccessTime />}
                            onClick={() => handleRating(transaction.id, 'none')}
                            disabled={submittingRating === transaction.id}
                            sx={{ minWidth: 80 }}
                          >
                            None
                          </Button>
                          {submittingRating === transaction.id && (
                            <CircularProgress size={20} />
                          )}
                        </Box>
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        {!userId 
                          ? "Rating unavailable"
                          : !allowRating 
                            ? "Not Rated"
                            : !isCurrentTransaction 
                              ? "Not Rated"
                              : "Not Rated"
                        }
                      </Typography>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>

      {/* Rating Dialog */}
      <RatingDialog
        open={ratingDialog.open}
        transactionId={ratingDialog.transactionId}
        rating={ratingDialog.rating}
        comment={ratingDialog.comment}
        submittingRating={submittingRating}
        onClose={handleDialogClose}
        onCommentChange={handleCommentChange}
        onSubmit={handleDialogSubmit}
      />
    </Box>
  );
};

export default TransactionRating;
